import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  Delete,
  Search,
  TrendingUp,
  TrendingDown,
  AdminPanelSettings,
  PersonRemove,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import userAPI from '../../services/userAPI';

const StudentsList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState(0); // 0: Tous, 1: Étudiants, 2: Admins
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filtrer les utilisateurs selon le terme de recherche et le rôle
    let filtered = users;

    // Filtre par rôle
    if (roleFilter === 1) {
      filtered = filtered.filter(u => u.role === 'student');
    } else if (roleFilter === 2) {
      filtered = filtered.filter(u => u.role === 'admin');
    }

    // Filtre par recherche
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        user =>
          user.name?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term)
      );
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('👥 Fetching all users...');
      
      // Récupérer TOUS les utilisateurs (pas de filtre par rôle)
      const response = await userAPI.getAll();
      console.log('✅ Users fetched:', response);
      
      const usersList = response.users || response.data?.users || [];
      setUsers(usersList);
      setFilteredUsers(usersList);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du chargement des utilisateurs';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('❌ Users error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleViewDetails = () => {
    if (selectedUser) {
      navigate(`/students/${selectedUser._id}`);
      handleMenuClose();
    }
  };

  const handleOpenPromoteDialog = () => {
    handleMenuClose();
    setPromoteDialogOpen(true);
  };

  const handleClosePromoteDialog = () => {
    setPromoteDialogOpen(false);
  };

  const handlePromoteToAdmin = async () => {
    if (!selectedUser) return;

    try {
      console.log('👑 Promoting user to admin:', selectedUser._id);
      const newRole = selectedUser.role === 'admin' ? 'student' : 'admin';
      
      await userAPI.update(selectedUser._id, { role: newRole });
      
      toast.success(
        newRole === 'admin' 
          ? `${selectedUser.name} est maintenant administrateur` 
          : `${selectedUser.name} est maintenant étudiant`
      );
      
      handleClosePromoteDialog();
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de la modification du rôle';
      toast.error(errorMsg);
      console.error('❌ Promote error:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedUser.name} ?`)) {
      handleMenuClose();
      return;
    }

    try {
      console.log('🗑️ Deleting user:', selectedUser._id);
      await userAPI.delete(selectedUser._id);
      toast.success('Utilisateur supprimé avec succès');
      fetchUsers();
      handleMenuClose();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(errorMsg);
      console.error('❌ Delete user error:', err);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 75) return 'success';
    if (rate >= 50) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Gestion des Utilisateurs
        </Typography>
        <Chip
          label={`${filteredUsers.length} utilisateur${filteredUsers.length > 1 ? 's' : ''}`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Barre de recherche */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Rechercher un utilisateur par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Filtres par rôle */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={roleFilter} onChange={(e, newValue) => setRoleFilter(newValue)}>
          <Tab label={`Tous (${users.length})`} />
          <Tab label={`Étudiants (${users.filter(u => u.role === 'student').length})`} />
          <Tab label={`Admins (${users.filter(u => u.role === 'admin').length})`} />
        </Tabs>
      </Card>

      {/* Tableau des étudiants */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Rôle</TableCell>
              <TableCell align="center">Tentatives</TableCell>
              <TableCell align="center">Score Moyen</TableCell>
              <TableCell align="center">Taux de Réussite</TableCell>
              <TableCell align="center">Dernière Activité</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    {searchTerm ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur inscrit'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow 
                  key={user._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/students/${user._id}`)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: user.role === 'admin' ? 'secondary.main' : 'primary.main' }}>
                        {getInitials(user.name)}
                      </Avatar>
                      <Typography variant="body1">
                        {user.name || 'Sans nom'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={user.role === 'admin' ? 'Administrateur' : 'Étudiant'}
                      color={user.role === 'admin' ? 'secondary' : 'default'}
                      size="small"
                      icon={user.role === 'admin' ? <AdminPanelSettings /> : null}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {user.role === 'student' ? (
                      <Chip
                        label={user.stats?.totalAttempts || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {user.role === 'student' ? (
                      <Typography variant="body1" fontWeight="bold">
                        {user.stats?.averageScore || 0}%
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {user.role === 'student' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Chip
                          label={`${user.stats?.successRate || 0}%`}
                          size="small"
                          color={getSuccessRateColor(user.stats?.successRate || 0)}
                          icon={
                            (user.stats?.successRate || 0) >= 50 
                              ? <TrendingUp /> 
                              : <TrendingDown />
                          }
                        />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" color="text.secondary">
                      {user.lastLogin 
                        ? new Date(user.lastLogin).toLocaleDateString('fr-FR')
                        : 'Jamais'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, user);
                      }}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu contextuel */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility sx={{ mr: 1 }} />
          Voir Détails
        </MenuItem>
        <MenuItem onClick={handleOpenPromoteDialog}>
          {selectedUser?.role === 'admin' ? (
            <>
              <PersonRemove sx={{ mr: 1 }} />
              Rétrograder en Étudiant
            </>
          ) : (
            <>
              <AdminPanelSettings sx={{ mr: 1 }} />
              Promouvoir Admin
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} />
          Supprimer
        </MenuItem>
      </Menu>

      {/* Dialog de confirmation promotion */}
      <Dialog
        open={promoteDialogOpen}
        onClose={handleClosePromoteDialog}
      >
        <DialogTitle>
          {selectedUser?.role === 'admin' ? 'Rétrograder en Étudiant' : 'Promouvoir Administrateur'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedUser?.role === 'admin' ? (
              <>
                Êtes-vous sûr de vouloir rétrograder <strong>{selectedUser?.name}</strong> en étudiant ?
                <br /><br />
                Cette personne perdra tous ses privilèges d'administrateur.
              </>
            ) : (
              <>
                Êtes-vous sûr de vouloir promouvoir <strong>{selectedUser?.name}</strong> en administrateur ?
                <br /><br />
                Cette personne aura accès à toutes les fonctionnalités d'administration.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePromoteDialog} color="inherit">
            Annuler
          </Button>
          <Button 
            onClick={handlePromoteToAdmin} 
            color={selectedUser?.role === 'admin' ? 'warning' : 'secondary'}
            variant="contained"
            autoFocus
          >
            {selectedUser?.role === 'admin' ? 'Rétrograder' : 'Promouvoir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentsList;
